/**
 * Per-entity-kind visual primitives (accent colour + icon component).
 *
 * Lifted out of ``EntityLinks.tsx`` so the topbar search popover, the
 * mention-link rows, and any future surface that needs to render an
 * entity with its kind tinting share one definition.
 */

import type { EntityKind } from "../api/links";
import { Icon } from "./icons";
import type { MentionKind } from "./MentionContext";

export const KIND_ACCENT: Record<EntityKind, string> = {
  note: "var(--c-yellow)",
  task: "var(--c-purple)",
  task_list: "var(--c-green)",
  event: "var(--c-blue)",
  habit: "var(--c-aqua)",
  recipe: "var(--c-red)",
  shopping_list: "var(--c-orange)",
  shopping_item: "var(--c-orange)",
  tag: "var(--c-fg)",
};

export const MENTION_TO_ENTITY: Record<MentionKind, EntityKind> = {
  note: "note",
  task: "task",
  event: "event",
  habit: "habit",
  shopping_list: "shopping_list",
  shopping_item: "shopping_item",
  recipe: "recipe",
};

/**
 * Map an entity ``(kind, id)`` to its router path. Mirrors ``mentionTo``
 * but takes the broader ``EntityKind`` union (includes ``task_list``
 * and ``tag``).
 *
 * Event paths fall back to ``/calendar`` because the caller usually
 * doesn't know the event's date; the calendar screen lands on today.
 * Tag links don't navigate yet — they return ``null`` and the caller
 * should treat them as non-clickable.
 */
export function entityPath(kind: EntityKind, id: string): string | null {
  switch (kind) {
    case "note":
      return `/notes/${id}`;
    case "task":
      return `/tasks?focus=${encodeURIComponent(id)}`;
    case "task_list":
      return `/tasks/${id}`;
    case "event":
      return "/calendar";
    case "habit":
      return `/habits/${id}`;
    case "recipe":
      return `/recipes/${id}`;
    case "shopping_list":
      return `/shopping/${id}`;
    case "shopping_item":
      return `/shopping?focus=${encodeURIComponent(id)}`;
    case "tag":
      return null;
  }
}

/** Per-kind icon component for the link row + search popover. */
export function KindIcon({ kind }: { kind: EntityKind }): JSX.Element {
  switch (kind) {
    case "note":
      return <Icon.note />;
    case "task":
    case "task_list":
      return <Icon.task />;
    case "event":
      return <Icon.cal />;
    case "habit":
      return <Icon.habit />;
    case "tag":
      return <Icon.hash />;
    case "shopping_list":
    case "shopping_item":
      return <Icon.cart />;
    case "recipe":
      return <Icon.chef />;
  }
}
